import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Instagram, Youtube, Send, Music, ArrowLeft } from "lucide-react";
import { api, resolveUrl } from "../lib/api";

export function ArtistsList() {
  const [artists, setArtists] = useState([]);
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("all");

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  }, []);

  const genres = useMemo(() => ["all", ...Array.from(new Set(artists.map((a) => a.genre)))], [artists]);
  const filtered = artists.filter(
    (a) => (genre === "all" || a.genre === genre) && a.name.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <section className="bg-[#0B1526] text-white py-24 sm:py-32" data-testid="artists-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">The Artists</div>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95]">
            Anointed voices, <br />
            <span className="italic text-[#C00000]">unafraid.</span>
          </h1>
        </div>
      </section>

      <section className="py-12 border-b border-gray-200 sticky top-20 bg-white z-20" data-testid="artists-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search artists..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 focus:border-[#1F3864] focus:outline-none text-sm"
              data-testid="input-artist-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {genres.map((g) => (
              <button
                key={g}
                onClick={() => setGenre(g)}
                className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                  genre === g ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-700 hover:border-[#1F3864]"
                }`}
                data-testid={`filter-genre-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {filtered.map((a) => (
            <Link key={a.id} to={`/artists/${a.id}`} className="group image-card aspect-[3/4]" data-testid={`artist-card-${a.id}`}>
              <img src={resolveUrl(a.photo_url)} alt={a.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1526] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C00000] mb-2">{a.genre}</div>
                <div className="font-display text-3xl mb-1">{a.name}</div>
                <div className="text-xs text-white/70 line-clamp-2 mt-2">{a.bio}</div>
              </div>
            </Link>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-20 text-gray-500">No artists match your search.</div>
          )}
        </div>
      </section>
    </div>
  );
}

export function ArtistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);

  useEffect(() => {
    api.get(`/artists/${id}`).then((r) => setArtist(r.data)).catch(() => navigate("/artists"));
  }, [id, navigate]);

  if (!artist) {
    return <div className="py-32 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div>
      <section className="relative h-[70vh] min-h-[500px] bg-[#0B1526]" data-testid="artist-hero">
        <img src={resolveUrl(artist.photo_url)} alt={artist.name} className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1526] via-[#0B1526]/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-16 text-white">
          <button onClick={() => navigate("/artists")} className="mb-6 inline-flex items-center gap-2 text-white/70 hover:text-white text-sm" data-testid="action-back-to-artists">
            <ArrowLeft className="w-4 h-4" /> Back to Artists
          </button>
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">{artist.genre}</div>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95]">{artist.name}</h1>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-4">Biography</div>
            <p className="text-lg leading-relaxed text-gray-700 whitespace-pre-line">{artist.bio}</p>

            {artist.videos && artist.videos.length > 0 && (
              <div className="mt-14">
                <h2 className="font-display text-3xl text-[#1F3864] mb-6 flex items-center gap-3">
                  <Music className="w-6 h-6 text-[#C00000]" /> Videos & Songs
                </h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  {artist.videos.map((v) => (
                    <Link key={v.id} to="/videos" className="block group" data-testid={`artist-video-${v.id}`}>
                      <div className="aspect-video overflow-hidden bg-gray-100">
                        <img src={resolveUrl(v.thumbnail)} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                      <h3 className="mt-3 font-display text-lg text-[#1F3864] group-hover:text-[#C00000]">{v.title}</h3>
                      <div className="text-xs text-gray-500 mt-1">{v.category}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="border border-gray-200 p-6">
              <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-3">Connect</div>
              <div className="flex gap-2">
                {artist.social_links?.instagram && (
                  <a href={artist.social_links.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 border border-gray-300 hover:bg-[#1F3864] hover:text-white hover:border-[#1F3864] flex items-center justify-center"><Instagram className="w-4 h-4" /></a>
                )}
                {artist.social_links?.youtube && (
                  <a href={artist.social_links.youtube} target="_blank" rel="noreferrer" className="w-10 h-10 border border-gray-300 hover:bg-[#1F3864] hover:text-white hover:border-[#1F3864] flex items-center justify-center"><Youtube className="w-4 h-4" /></a>
                )}
              </div>
            </div>
            <Link to="/contact" className="btn-primary-red w-full justify-center" data-testid="action-book-artist-button">
              <Send className="w-4 h-4" /> Book This Artist
            </Link>
            <div className="text-sm text-gray-500 leading-relaxed">
              For booking enquiries and collaborations, head to our contact form and select
              "Booking" from the subject dropdown.
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
